import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Megaphone, Calendar, Users, UserCog, Image as ImageIcon, MessageSquare, Star, FileText, Settings, LogOut, User, Menu, X, Eye, ClipboardList, FolderOpen, PanelsTopLeft } from "lucide-react";
import { useAuth } from "../../lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/slider", label: "Slider Management", icon: PanelsTopLeft },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/events", label: "Events & CME", icon: Calendar },
  { to: "/admin/committee", label: "Organization Structure", icon: UserCog },
  { to: "/admin/members", label: "Members Directory", icon: Users },
  { to: "/admin/media", label: "Media Library", icon: FolderOpen },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/downloads", label: "Downloads", icon: FileText },
  { to: "/admin/applications", label: "Applications", icon: ClipboardList },
  { to: "/admin/messages", label: "Contact Messages", icon: MessageSquare },
  { to: "/admin/settings", label: "Website Settings", icon: Settings },
  { to: "/admin/profile", label: "Profile", icon: User },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="flex h-screen bg-brand-bg overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-brand-sidebar text-slate-300 flex flex-col transform ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform duration-300`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white font-serif font-bold text-sm">GOS</div>
            <div>
              <div className="text-sm font-serif font-bold text-white">GOS Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Control Panel</div>
            </div>
          </Link>
          <button className="md:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 scroll-thin">
          {NAV.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-colors ${isActive ? "bg-brand-primary text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
              <it.icon size={18} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400">Signed in as</div>
          <div className="text-sm font-semibold text-white truncate">{user?.email}</div>
          <button onClick={handleLogout} className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm" data-testid="admin-logout">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-y-auto scroll-thin">
        <header className="h-16 bg-white border-b border-brand-border flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-brand-text" onClick={() => setOpen(true)}><Menu size={20} /></button>
            <div className="hidden md:block text-sm text-brand-muted">
              <span>Welcome back,</span> <span className="text-brand-text font-semibold">{user?.name || "Admin"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-brand-primary font-semibold hover:text-brand-primary-hover"><Eye size={15} /> View Site</Link>
            <div className="w-9 h-9 rounded-full bg-brand-primary text-white flex items-center justify-center font-semibold text-sm">{(user?.name || "A").charAt(0)}</div>
          </div>
        </header>
        <main className="p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
